import styled from '@emotion/styled';
import noop from 'lodash/noop';
import type { FunctionComponent } from 'react';
import { useCallback, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobProfileComponent } from '~/shared/data-contract/job-profile';
import type { JobProfileSkill } from '~/shared/data-contract/skill';
import { Button } from '~/ui/core/button/Button';

import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';
import { useManualEditStore } from './ManualEditStoreProvider';
import type { PendingSuggestedSkill } from './skill-action-store';
import { SkillChipWithProficiencyLabel } from './skills/SkillList';
import { useSkillActions } from './use-skill-actions';

const SectionHeadlineWithIconWrapper = styled.div`
    align-items: center;
    display: flex;
    gap: ${({ theme }) => theme.spacing.m};
`;

const SectionHeadline = styled.p`
    color: ${({ theme }) => theme.colors.black[900]};
    font-family: ${({ theme }) => theme.typography.body.S.semibold.fontFamily};
    font-size: ${({ theme }) => theme.typography.body.S.semibold.fontSize};
    font-weight: ${({ theme }) => theme.typography.body.S.semibold.fontWeight};
    line-height: ${({ theme }) => theme.typography.body.S.semibold.lineHeight};
    margin: 0;
    word-wrap: break-word;
`;

const SectionActionTitle = styled.h2`
    color: ${({ theme }) => theme.colors.gray[700]};
    font-family: ${({ theme }) => theme.typography.caption.S.semibold.fontFamily};
    font-size: ${({ theme }) => theme.typography.caption.S.semibold.fontSize};
    font-weight: ${({ theme }) => theme.typography.caption.S.semibold.fontWeight};
    line-height: ${({ theme }) => theme.typography.caption.S.semibold.lineHeight};
    text-transform: ${({ theme }) => theme.typography.caption.S.semibold.textTransform};
    margin: 0;
`;

const SkillsSuggestionSectionWrapper = styled.div`
    background: ${({ theme }) => theme.colors.gray[50]};
    border: 1px solid ${({ theme }) => theme.colors.orange[800]};
    border-radius: ${({ theme }) => theme.borderRadius.l};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xxl};
    padding: ${({ theme }) => theme.spacing.xxl};
`;

const SkillWithCheckboxWrapper = styled.div`
    align-items: center;
    display: flex;
    gap: ${({ theme }) => theme.spacing.xxl};
`;

const SuggestionsByActionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-right: 1.1rem;
`;

const SuggestionsContainer = styled.div`
    margin-top: ${({ theme }) => theme.spacing.xxl};
    gap: ${({ theme }) => theme.spacing.xxl};
    display: flex;
    flex-direction: column;
`;

const StyledCheckbox = styled.input`
    &:checked {
        background-color: ${({ theme }) => theme.colors.gray[800]};
    }

    &:checked::after {
        border: solid ${({ theme }) => theme.colors.white[0]};
        border-width: 0 1px 1px 0;
        content: '';
        height: 8px;
        left: 5px;
        position: absolute;
        top: 2px;
        transform: rotate(45deg);
        width: 4px;
    }

    accent-color: ${({ theme }) => theme.colors.gray[800]};
    appearance: none;
    background-color: ${({ theme }) => theme.colors.white[0]};
    border: 1px solid ${({ theme }) => theme.colors.gray[800]};
    border-radius: ${({ theme }) => theme.borderRadius.s};
    height: ${({ theme }) => theme.spacing.xxl};
    min-width: ${({ theme }) => theme.spacing.xxl};
    position: relative;
    width: ${({ theme }) => theme.spacing.xxl};
`;

const SkillsAiSuggestionsWrapper = styled.div`
    & > :last-child {
        margin-top: -${({ theme }) => theme.spacing.xs};
    }

    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xxl};
    padding-bottom: ${({ theme }) => theme.spacing.l};
`;

const PendingSuggestionSectionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xxl};
`;

const ButtonsContainer = styled.div`
    display: grid;
    gap: ${({ theme }) => theme.spacing.m};
    grid-template-columns: 1fr 2fr;
`;

const SuggestionsHeadline: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);

    return (
        <SectionHeadlineWithIconWrapper>
            <SectionHeadline>{translate('pages.job-architecture-v2.adjust-with-ai')}</SectionHeadline>
        </SectionHeadlineWithIconWrapper>
    );
};

type CheckboxProps = {
    readonly checked: boolean;
    readonly onChange: () => void;
};

const Checkbox: FunctionComponent<CheckboxProps> = ({ checked, onChange }) => {
    return <StyledCheckbox checked={checked} type="checkbox" onChange={onChange} />;
};

type SkillsSuggestionSectionProps = {
    readonly headline: string;
    readonly onToggleAcceptance: (suggestedSkill: PendingSuggestedSkill) => void;
    readonly suggestedSkills: readonly PendingSuggestedSkill[];
};

export const SkillsSuggestionSection: FunctionComponent<SkillsSuggestionSectionProps> = ({
    headline,
    onToggleAcceptance,
    suggestedSkills,
}) => {
    const { openSkillTile } = useJobArchitectureUrlTileActions();

    if (suggestedSkills.length === 0) {
        return null;
    }

    return (
        <SuggestionsByActionWrapper>
            <SectionActionTitle>{headline}</SectionActionTitle>
            <SuggestionsContainer>
                {suggestedSkills.map((suggestedSkill) => (
                    <SkillWithCheckboxWrapper key={suggestedSkill.skill.id}>
                        <Checkbox
                            checked={suggestedSkill.isAccepted}
                            onChange={() => {
                                onToggleAcceptance(suggestedSkill);
                            }}
                        />
                        <SkillChipWithProficiencyLabel
                            commentable={false}
                            isEditMode={false}
                            skill={suggestedSkill.skill}
                            onChangeProficiencyLevel={noop}
                            onClick={() => {
                                openSkillTile(suggestedSkill.skill.id, undefined, suggestedSkill.skill.level);
                            }}
                            onRemoveSkill={noop}
                        />
                    </SkillWithCheckboxWrapper>
                ))}
            </SuggestionsContainer>
        </SuggestionsByActionWrapper>
    );
};

type SkillsActionButtonsProps = {
    readonly acceptSuggestions: () => void;
    readonly rejectSuggestions: () => void;
};

const SkillsActionButtons: FunctionComponent<SkillsActionButtonsProps> = ({ acceptSuggestions, rejectSuggestions }) => {
    const { translate } = useContext(ApplicationContext);

    return (
        <ButtonsContainer>
            <Button size="sm" variant="tertiary" onClick={rejectSuggestions}>
                {translate('pages.job-architecture-v2.job-profile-reject-changes')}
            </Button>
            <Button size="sm" onClick={acceptSuggestions}>
                {translate('pages.job-architecture-v2.job-profile-accept-changes')}
            </Button>
        </ButtonsContainer>
    );
};

type SkillsAiSuggestionsProps = {
    readonly architectureId?: string;
    readonly currentSkills: readonly JobProfileSkill[];
    readonly onAcceptSkillSuggestions: (skills: readonly JobProfileSkill[]) => void;
    readonly profile: JobProfileComponent;
};

export const SkillsAiSuggestions: FunctionComponent<SkillsAiSuggestionsProps> = ({
    architectureId = '',
    currentSkills,
    onAcceptSkillSuggestions,
    profile,
}) => {
    const { translate } = useContext(ApplicationContext);
    const resetManualEdits = useManualEditStore((state) => state.resetManualEdits);
    const {
        discardPendingSkillSuggestions,
        executePendingSkillSuggestions,
        pendingSkillSuggestions,
        toggleAcceptanceOfPendingSuggestedSkill,
    } = useSkillActions({ architectureId, currentSkills, onAcceptSkillSuggestions, profile });

    const handleAcceptSuggestions = useCallback(() => {
        executePendingSkillSuggestions();
        resetManualEdits();
    }, [executePendingSkillSuggestions, resetManualEdits]);

    const handleRejectSuggestions = useCallback(() => {
        discardPendingSkillSuggestions();
        resetManualEdits();
    }, [discardPendingSkillSuggestions, resetManualEdits]);

    if (pendingSkillSuggestions.isNothing === true) {
        return null;
    }

    const { add: addSkills, adjust: adjustSkills, remove: removeSkills } = pendingSkillSuggestions.value;

    return (
        <SkillsAiSuggestionsWrapper>
            <SkillsSuggestionSectionWrapper>
                <SuggestionsHeadline />
                <PendingSuggestionSectionWrapper>
                    {addSkills.length > 0 && (
                        <SkillsSuggestionSection
                            headline={translate('pages.job-architecture-v2.job-profile-skill-suggestions-add-label')}
                            suggestedSkills={addSkills}
                            onToggleAcceptance={toggleAcceptanceOfPendingSuggestedSkill}
                        />
                    )}
                    {adjustSkills.length > 0 && (
                        <SkillsSuggestionSection
                            headline={translate(
                                'pages.job-architecture-v2.job-profile-skill-suggestions-adjust-skills-label',
                            )}
                            suggestedSkills={adjustSkills}
                            onToggleAcceptance={toggleAcceptanceOfPendingSuggestedSkill}
                        />
                    )}
                    {removeSkills.length > 0 && (
                        <SkillsSuggestionSection
                            headline={translate('pages.job-architecture-v2.job-profile-skill-suggestions-remove-label')}
                            suggestedSkills={removeSkills}
                            onToggleAcceptance={toggleAcceptanceOfPendingSuggestedSkill}
                        />
                    )}
                </PendingSuggestionSectionWrapper>
            </SkillsSuggestionSectionWrapper>
            <SkillsActionButtons
                acceptSuggestions={handleAcceptSuggestions}
                rejectSuggestions={handleRejectSuggestions}
            />
        </SkillsAiSuggestionsWrapper>
    );
};
